// The platform console (design §2, Q3): eSignIt staff approve new companies (which creates their key), pause or
// resume them, and set plans by hand until Stripe (D4). No document access.
import { useMemo, useState } from "react";
import { Building2, Check, Pause, Play, Search, ShieldCheck } from "lucide-react";
import { api, useApi } from "../lib/api";
import { shortDate, when } from "../lib/format";
import type { Plan, PlatformCompany } from "../lib/types";
import { Chip, inputCls } from "../components/bits";
import { Button, cx, Empty, ErrorNote, PromptDialog, Spinner, toast } from "../components/ui";

export default function Platform() {
  const { data, error, loading, reload } = useApi<{ companies: PlatformCompany[]; plans: Plan[] }>("/platform/companies");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [pausing, setPausing] = useState<PlatformCompany | null>(null);
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (data?.companies ?? []).filter((c) => !n || c.name.toLowerCase().includes(n) || c.createdBy.includes(n) || c.cid.includes(n));
  }, [data, q]);
  const set = async (c: PlatformCompany, body: Record<string, string>, ok: string) => {
    setBusy(c.cid);
    try {
      await api(`/platform/companies/${c.cid}`, { body });
      toast(ok);
      await reload();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  const pending = (data?.companies ?? []).filter((c) => c.status === "pending").length;
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-xl font-semibold flex items-center gap-2 mb-1">
        <ShieldCheck className="size-5" /> Platform
      </h1>
      <p className="text-sm text-muted mb-5">Every company on eSignIt. Approving a company lets it send and creates its encryption key. You never see their documents.</p>
      <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:items-center">
        <div className="relative sm:w-72">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Company, owner or id" aria-label="Search" className={cx(inputCls, "pl-9")} />
        </div>
        {pending > 0 && <Chip tone="warn">{pending} waiting for approval</Chip>}
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : !list.length ? (
        <Empty icon={<Building2 className="size-10" />} title="No companies yet" />
      ) : (
        <div className="bg-surface border border-line rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Company</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 font-medium">This month</th>
                <th className="px-4 py-2 font-medium">People</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((c) => (
                <tr key={c.cid}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted">
                      {c.createdBy} · registered {shortDate(c.createdAt)} · <span className="font-mono">{c.cid}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {c.status === "active" ? <Chip tone="good">Active</Chip> : c.status === "pending" ? <Chip tone="warn">Pending {when(c.createdAt)}</Chip> : <Chip tone="bad">Paused</Chip>}
                    {c.suspendReason && c.status === "suspended" && <div className="text-xs text-muted mt-0.5 max-w-48 truncate" title={c.suspendReason}>{c.suspendReason}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <select aria-label={`Plan for ${c.name}`} value={c.plan} disabled={busy === c.cid} onChange={(e) => set(c, { plan: e.target.value }, "Plan changed")} className={cx(inputCls, "h-8")} style={{ width: "8rem" }}>
                      {data!.plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2.5 text-ink-2 whitespace-nowrap tabular">
                    {c.usage.envelopes} envelopes
                    <div className="text-xs text-muted">
                      {c.usage.sent} emails · {c.usage.bounced} bounced · {c.usage.complained} spam
                    </div>
                  </td>
                  <td className="px-4 py-2.5 tabular">{c.members}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      {c.status === "pending" && (
                        <Button variant="primary" className="h-8" busy={busy === c.cid} onClick={() => set(c, { status: "active" }, `${c.name} approved`)}>
                          <Check className="size-3.5" /> Approve
                        </Button>
                      )}
                      {c.status === "suspended" && (
                        <Button className="h-8" busy={busy === c.cid} onClick={() => set(c, { status: "active" }, `${c.name} resumed`)}>
                          <Play className="size-3.5" /> Resume
                        </Button>
                      )}
                      {c.status !== "suspended" && (
                        <Button className="h-8" onClick={() => setPausing(c)}>
                          <Pause className="size-3.5" /> Pause
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pausing && <PromptDialog title={`Pause ${pausing.name}?`} label="Reason (the owner is told)" confirm="Pause sending" onClose={() => setPausing(null)} onSubmit={(reason) => set(pausing, { status: "suspended", reason }, `${pausing.name} paused`)} />}
    </div>
  );
}
